import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  SIGN_RETURN_MESSAGE_SOURCE,
  signReturnOutcomeFromEvent,
  type SignReturnMessage,
} from '@/pages/sign/signReturnMessage'

/**
 * DocuSign embedded-signing return page.
 *
 * ``createRecipientView`` requires a server-configured ``returnUrl``; DocuSign
 * redirects the signing iframe here (``/sign/return?order_id=…&event=…``) when
 * the ceremony ends.
 * Intentionally standalone and public: a nested, full-page iframe redirect
 * cannot satisfy an auth guard, and this page carries no order data, PHI, or API
 * calls — it only reads the outcome DocuSign hands back in the query string
 * (which is spoofable; DocuSign Connect remains the source of truth, this page
 * is UX only).
 *
 * It is mounted **before any provider** — see the early return in ``App`` — and
 * must stay that way. This is a fresh document inside the ceremony iframe, so
 * anything it mounts is a second copy of that machinery running three frames
 * deep. Auth0 in particular cannot complete silent authentication from there,
 * and answers ``login_required``, which auth0-spa-js handles by clearing the
 * token cache. That cache is ``localStorage``, shared by every document on the
 * origin, so the frame would log out the very session the clinician is holding
 * in the window above it.
 *
 * Which leaves it nothing to decide with, and nothing it needs to decide. It
 * posts the outcome up to the signing page that frames it — same origin, and
 * still holding the session, the order, and the completion UI — and that page
 * takes it from there. The confirmation below is for the case where there is no
 * parent to tell: a return frame opened on its own, where the manual link is
 * the only way onward.
 */

const parentLinkClass =
  'inline-flex items-center justify-center rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700'

export function SignReturn() {
  const [searchParams] = useSearchParams()

  const orderId = searchParams.get('order_id')?.trim() ?? ''
  const outcome = signReturnOutcomeFromEvent(searchParams.get('event'))

  // Absolute, same-origin destination. When no order id came back, fall back to
  // the orders list rather than a broken order route.
  const orderUrl = useMemo(() => {
    const origin = window.location.origin
    return orderId ? `${origin}/order/in-progress/${encodeURIComponent(orderId)}` : `${origin}/orders`
  }, [orderId])

  useEffect(() => {
    // ``window.parent`` rather than ``window.top``: this SPA is itself framed by
    // Epic Hyperspace, and the page that needs to hear this is the signing page
    // directly above, not whatever sits at the top of the stack.
    const parent = window.parent
    if (!parent || parent === window) return
    const message: SignReturnMessage = { source: SIGN_RETURN_MESSAGE_SOURCE, outcome, orderId }
    try {
      parent.postMessage(message, window.location.origin)
    } catch {
      // Nothing to fall back to and nothing lost: DocuSign posts its own
      // end-of-ceremony message to the same window, and the manual link below
      // stays on screen either way.
    }
  }, [outcome, orderId])

  const title =
    outcome === 'complete'
      ? 'Signature completed'
      : outcome === 'cancelled'
        ? 'Signing was not completed'
        : 'Signing session ended'
  const body =
    outcome === 'complete'
      ? 'Your signature is being recorded. It may take a moment to appear on the order.'
      : outcome === 'cancelled'
        ? 'You can start signing again from the order.'
        : 'If you completed signing, it may take a moment to appear on the order.'

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">{title}</h1>
      <p className="text-sm font-medium text-text-body-subtle dark:text-gray-500">{body}</p>
      {/* target="_parent" breaks out of the ceremony iframe. Anyone without a
          session lands on the order route's guard, which is the sign-in path. */}
      <a href={orderUrl} target="_parent" className={parentLinkClass}>
        Go to the order
      </a>
    </div>
  )
}

export default SignReturn
